import { useMethod } from '@roc-digital/ui-lib';
import { FeFile } from './types';
import { archiveFile, deleteFile, listFiles, renameFile, uploadFiles } from '@/logic/files';
import { FilesTable } from './FilesTable';
import { FilesTiles } from './FilesTiles';
import { InputSearch } from '../InputSearch';
import { Button } from '../Button';
import React from 'react';
import { toast } from 'react-toastify';
import { prompt } from './PromptModal';
import { confirm } from './ConfirmModal';
import { Pagination as Paginator } from '@roc-digital/mxm-base/data';
import { Pagination } from '../Pagination';
import { Icon } from '../Icon';

export interface FileExplorerProps {
  onSelectFiles?(file: FeFile): void;
  selectedFile?: FeFile;
  select?: boolean;
  maxHeight?: string;
}

interface FileFilters {count: number, page: number, query?: string}

const defaultFilters: FileFilters = {
  count: 10,
  page: 1
}

export function FileExplorer(props: FileExplorerProps) {
  const [search, setSearch] = React.useState('');
  const listQuery = useMethod(listFiles, {
    autoRun: [defaultFilters],
  });
  const [display, setDisplay] = React.useState('tiles' as 'tiles' | 'table');
  const ListComponent = display === 'tiles' ? FilesTiles : FilesTable;

  React.useEffect(() => {
    if(!search && !listQuery.lastArgs?.[0]?.query) {
      return;
    }
    const filters: FileFilters = {... defaultFilters, query: search};
    
    listQuery.debounce([filters], 500);
  }, [search])

  const startNewFileUpload = async () => {
    const files = await selectFiles();
    uploadFiles(files);
  }

  const fileRef = React.useRef<FeFile>(null)

  const renameQuery = useMethod(renameFile, {
    async onBefore(args) {
      let [name, ext] = (fileRef.current?.name || '').split('.', 2);
      name = await prompt('Enter new name for file:', 'Rename File', name);
      if(!name) return undefined;
      args[1] = name + '.' + ext;
      return args;
    },
    onSuccess: () => {
      toast.success('File renamed.');
      listQuery.reload();
    },
     onError: () => {
      toast.error('Error deleting file')
     }
  });
  const archiveQuery = useMethod(archiveFile, {
    async onBefore(args) {
      if(!await confirm(<>
        Confirm to archive file:  <b> 
        - {fileRef.current?.name} </b>
        </>, 'Archive File')) {
        return undefined;
      }
      return args;
    },
    onSuccess: () => {
      toast.success('File archived.');
      listQuery.reload();
    },
     onError: () => {
      toast.error('Error deleting file')
     }
  });
  const deleteQuery = useMethod(deleteFile, {
    
    async onBefore(args) {
      if(!await confirm(<>
        Are you sure you want to delete this file?  <b> 
        - {fileRef.current?.name} </b>
        </>, 'Delete File')) {
        return undefined;
      }
      return args;
    },
    onSuccess: () => {
      toast.success('File deleted.');
      listQuery.reload();
    },
     onError: () => {
      toast.error('Error deleting file.')
     }
  });

  const handleAction = (action: 'rename' | 'archive' | 'delete', fe: FeFile) => {
    //@ts-ignore
    fileRef.current = fe;

    if(action === 'archive') {
      archiveQuery.run(fe.id, true);
    } else if(action === 'delete') {
      deleteQuery.run(fe.id);
    } else if(action === 'rename') {
      renameQuery.run(fe.id, fe.name);
    }
  }

  const page = React.useMemo(() => {
    if (!listQuery.result?.paging) return null;

    const { count, limit, page, total, pages } = listQuery.result?.paging;
    return new Paginator(pages, page, total, limit, count);
  }, [listQuery.result?.paging]);

  return <div className='flex flex-col gap-4'>
    <div className="flex flex-row justify-between">
      <div className="w-1/2 pr-8 flex flex-row justify-between items-center gap-4 w-full">
        <InputSearch
          showLoading={false}
          showLabel={false}
          placeholder={'Search Images'}
          hideResults
          value={search}
          changeValueEvent={setSearch}
        />

        <div className="flex flex-row justify-between items-center gap-1" title='Change display mode.'>
          <Icon src={rowsIcon} clickEvent={() => setDisplay('table')} className={display === 'table' ? 'bg-[#24aae1] rounded' : ''}/>
          <Icon src={tilesIcon} clickEvent={() => setDisplay('tiles')} className={display === 'tiles' ? 'bg-[#24aae1] rounded' : ''}/>
        </div>
        <Button size='md' variant='primary' clickEvent={startNewFileUpload}>Upload Images</Button>
        <Button size='md' variant='square' clickEvent={() => listQuery.reload()}>Refresh</Button>
        <input id="imagesFileInput" type="file" multiple style={{display: 'none'}} accept="image/*"/>
      </div>
    </div>
    <div style={props.maxHeight ? {maxHeight: props.maxHeight, overflowY: 'auto', backgroundColor: 'white', 'padding': '8px'} : undefined}>
      <ListComponent
        files={listQuery.result?.data || []}
        loading={listQuery.loading}
        onAction={handleAction}
        select={props.select}
        selectedFile={props.selectedFile}
        onSelectFiles={props.onSelectFiles}
      />
      {page && (
        <Pagination
          className={'mt-5'}
          paginateEvent={(event) => {
            const filters = {...listQuery.lastArgs?.[0] || defaultFilters};
            filters.page = event.page;
            listQuery.run(filters);
          }}
          paginator={page} 
        />
      )}
    </div>
    
  </div>
}


async function selectFiles() {
  return new Promise<File[]>((res, rej) => {
    const input = document.getElementById('imagesFileInput') as HTMLInputElement;
    if(!input) {
      return res([]);
    }
    input?.addEventListener('change', (event) => {
      res(Array.from(input.files || []));
    }, {once: true});
    input.click();
  });
}


const rowsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256"><path d="M208,136H48a16,16,0,0,0-16,16v40a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V152A16,16,0,0,0,208,136Zm0,56H48V152H208v40Zm0-144H48A16,16,0,0,0,32,64v40a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V64A16,16,0,0,0,208,48Zm0,56H48V64H208v40Z"></path></svg>`;
const tilesIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256"><path d="M200,40H56A16,16,0,0,0,40,56V200a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm0,80H136V56h64ZM120,56v64H56V56ZM56,136h64v64H56Zm144,64H136V136h64v64Z"></path></svg>`;